import React, { useCallback, useEffect, useState } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import { Tab, Tabs } from '@app/react-common/components/tabs/Tabs';
import { useAppContext } from '@app/react-common/AppContext';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTest } from '../../models/AbTest';
import { AbTestTypeCategory, AbTestWidgetType } from '../../models/AbTestCreationType';
import { AbTestFormData, getInitialFormData } from './shared/abTestModalUtils';
import { AbTestModalHeader } from './shared/AbTestModalHeader';
import { AbTestModalDetailsTab } from './shared/AbTestModalDetailsTab';
import { AbTestModalTrafficAllocationTab } from './shared/AbTestModalTrafficAllocationTab';
import { useAbTestModalCommon } from './shared/useAbTestModalCommon';
import useCheckoutProfiles from './hooks/useCheckoutProfiles';
import { CheckoutProfileAllocationSection } from './components/CheckoutProfileAllocationSection/CheckoutProfileAllocationSection';
import { buildCheckoutProfileVariants } from './components/CheckoutProfileAllocationSection/checkoutProfileVariants';
import { CheckoutProfileTokenTab } from './components/CheckoutProfileTokenTab/CheckoutProfileTokenTab';

interface AbTestModalCheckoutProfileProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  onTestTypeChange?: (testType: AbTestTypeCategory) => void;
}

const TOKEN_TAB = 'Token for Shopify';
const TAB_ORDER = ['Details', 'Traffic allocation', TOKEN_TAB];
const TRAFFIC_ALLOCATION_TITLE = "Designate how much traffic you'd like to allocate to each profile";

export const AbTestModalCheckoutProfile: React.FC<AbTestModalCheckoutProfileProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  saveTest,
  existingAbTest,
  mode,
  onTestTypeChange,
}) => {
  const { shop$, showToast } = useAppContext();
  const [activeTab, setActiveTab] = useState('Details');
  const [shop, setShop] = useState<Shop>(null);
  const [trafficAllocationValid, setTrafficAllocationValid] = useState(false);
  const [statisticalSignificanceError, setStatisticalSignificanceError] = useState<string | undefined>(undefined);
  const [fullFormValid, setFullFormValid] = useState(false);
  const [showInvalidStep, setShowInvalidStep] = useState(0);
  const [formData, setFormData] = useState<AbTestFormData>(getInitialFormData(existingAbTest));
  const [testCreated, setTestCreated] = useState(mode === 'edit' || mode === 'view');
  const [savedAbTestId, setSavedAbTestId] = useState<number | null>(existingAbTest?.id || null);
  const { checkoutProfiles, loading: checkoutProfilesLoading } = useCheckoutProfiles(isOpen);

  const isCreating = mode === 'create' || mode === 'duplicate';

  useEffect(() => {
    if (isOpen) {
      setShowInvalidStep(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (checkoutProfilesLoading) return;
    setFormData((prevData) => ({
      ...prevData,
      abTestVariants: buildCheckoutProfileVariants(checkoutProfiles, existingAbTest, mode),
    }));
  }, [checkoutProfiles, checkoutProfilesLoading, existingAbTest, mode]);

  const handleAfterSave = useCallback(() => {
    if (isCreating && activeTab !== TOKEN_TAB) {
      setActiveTab(TOKEN_TAB);
    } else {
      onSaveSuccess();
    }
  }, [isCreating, activeTab, onSaveSuccess]);

  const {
    getNextTab,
    getPreviousTab,
    handleInputChange,
    setFormDataValue,
    handleSave,
    handleTabChange,
    handleDetailsValidation,
  } = useAbTestModalCommon({
    mode,
    formData,
    setFormData,
    rulesValid: true,
    setRulesValid: () => {},
    setStatisticalSignificanceError,
    statisticalSignificanceError,
    widgetType: AbTestWidgetType.CHECKOUT_PROFILE,
    shop,
    existingAbTest,
    saveTest,
    onSaveSuccess,
    onClose,
    activeTab,
    setActiveTab,
    TAB_ORDER,
    showToast,
    onAfterSave: handleAfterSave,
    setTestCreated,
    setSavedAbTestId,
  });

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      if (shop) {
        setShop(shop);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [shop$]);

  useEffect(() => {
    setFullFormValid(trafficAllocationValid && formData?.name?.length > 0);
  }, [formData.name, trafficAllocationValid]);

  const isNextTabTokenForShopify = useCallback(
    (currentTabName: string): boolean => getNextTab(currentTabName) === TOKEN_TAB,
    [getNextTab],
  );

  const handleTabChangeWithValidation = useCallback(
    (tab: string) => {
      if (tab === TOKEN_TAB && !testCreated) {
        return;
      }
      if (isCreating && activeTab === TOKEN_TAB) {
        return;
      }
      if (activeTab === 'Traffic allocation' && mode !== 'view' && !trafficAllocationValid) {
        setShowInvalidStep(3);
        return;
      }
      handleTabChange(tab, setShowInvalidStep);
    },
    [testCreated, isCreating, activeTab, mode, trafficAllocationValid, handleTabChange],
  );

  const handleDetailsValidationCallback = useCallback(
    (tab: string) => handleDetailsValidation(tab, setShowInvalidStep),
    [handleDetailsValidation],
  );

  const handleTokenTabDone = useCallback(() => {
    if (mode === 'edit') {
      handleSave();
    } else {
      onSaveSuccess();
    }
  }, [mode, handleSave, onSaveSuccess]);

  const renderTabContent = useCallback(
    (tabName: string) => {
      switch (tabName) {
        case 'Details':
          return (
            <AbTestModalDetailsTab
              formData={formData}
              mode={mode}
              showInvalidStep={showInvalidStep}
              statisticalSignificanceError={statisticalSignificanceError}
              setStatisticalSignificanceError={setStatisticalSignificanceError}
              setFormDataValue={setFormDataValue}
              handleInputChange={handleInputChange}
              onClose={onClose}
              getNextTab={getNextTab}
              handleTabChange={handleTabChangeWithValidation}
              handleDetailsValidation={handleDetailsValidationCallback}
              hideCheckoutsLimit={true}
            />
          );
        case 'Traffic allocation':
          return (
            <AbTestModalTrafficAllocationTab
              widgetType={AbTestWidgetType.CHECKOUT_PROFILE}
              mode={mode}
              showInvalidStep={showInvalidStep}
              trafficAllocationValid={trafficAllocationValid}
              fullFormValid={fullFormValid}
              getPreviousTab={getPreviousTab}
              getNextTab={getNextTab}
              isNextTabTokenForShopify={isNextTabTokenForShopify}
              setActiveTab={setActiveTab}
              handleSave={handleSave}
              titleText={TRAFFIC_ALLOCATION_TITLE}
              trafficAllocationContent={
                <CheckoutProfileAllocationSection
                  formData={formData}
                  setFormData={setFormData}
                  setTrafficAllocationValid={setTrafficAllocationValid}
                  mode={mode}
                  checkoutProfiles={checkoutProfiles}
                  isLoading={checkoutProfilesLoading}
                />
              }
              setShowInvalidStep={setShowInvalidStep}
              onSaveSuccess={onSaveSuccess}
            />
          );
        case TOKEN_TAB:
          return <CheckoutProfileTokenTab savedAbTestId={savedAbTestId} onDone={handleTokenTabDone} />;
        default:
          return null;
      }
    },
    [
      formData,
      mode,
      showInvalidStep,
      statisticalSignificanceError,
      setFormDataValue,
      handleInputChange,
      onClose,
      getNextTab,
      handleTabChangeWithValidation,
      handleDetailsValidationCallback,
      trafficAllocationValid,
      fullFormValid,
      getPreviousTab,
      isNextTabTokenForShopify,
      handleSave,
      checkoutProfiles,
      checkoutProfilesLoading,
      onSaveSuccess,
      savedAbTestId,
      handleTokenTabDone,
    ],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="ab-test-modal">
        <AbTestModalHeader
          mode={mode}
          testTypeCategory={AbTestTypeCategory.CHECKOUT_PROFILE}
          onClose={onClose}
          onTestTypeChange={onTestTypeChange}
        />
        <Tabs
          activeTab={activeTab}
          setActiveTab={handleTabChangeWithValidation}
          clickableTabs={true}
          horizontalLineUnderTabs={true}
          scrollable={true}>
          {TAB_ORDER.map((tabName) => (
            <Tab key={tabName} label={tabName} showTab={true}>
              {renderTabContent(tabName)}
            </Tab>
          ))}
        </Tabs>
      </div>
    </Modal>
  );
};
